import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Icon,
  Link as ChakraLink,
  useColorModeValue,
  useColorMode,
  Button,
  Collapse,
  Divider,
} from '@chakra-ui/react';
import {
  FaTachometerAlt,
  FaUsers,
  FaClipboardList,
  FaPoll,
  FaEnvelope,
  FaPuzzlePiece,
  FaRegCalendarAlt,
  FaUser,
  FaWrench,
  FaNewspaper,
  FaBuilding,
  FaUserFriends,
  FaTable
} from 'react-icons/fa';
import { HiInboxArrowDown } from 'react-icons/hi2';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import {
  getDefaultAuthenticatedRoute,
  getNetworkMembership,
  getNetworkMemberships,
  getUserRoles,
  isConsultantUser,
  isNetworkUser,
  isOrgScopedUser,
  isOrganizationAdmin,
} from '../../utils/authRoles';
import {
  canAccessAdminSection,
  canManageConsultantsAdmin,
  canManageNetwork,
  canViewLeadsAdmin,
} from '../../utils/authorization';

const NavItem = ({ href, icon, label, hoverBgColor, isActive = false, external = false, tone = 'default' }) => {
  const activeBgColor = useColorModeValue('teal.50', 'whiteAlpha.200');
  const activeTextColor = useColorModeValue('teal.700', 'teal.200');
  const inactiveTextColor = useColorModeValue('gray.700', 'gray.200');
  const mutedIconColor = useColorModeValue('gray.500', 'gray.400');
  const environmentBgColor = useColorModeValue('orange.50', 'orange.900');
  const environmentTextColor = useColorModeValue('orange.700', 'orange.200');
  const isEnvironment = tone === 'environment';

  const itemHoverBgColor = isEnvironment ? environmentBgColor : hoverBgColor;
  const itemTextColor = isActive ? activeTextColor : isEnvironment ? environmentTextColor : inactiveTextColor;

  return (
    <ChakraLink
      href={href}
      _hover={{ textDecoration: 'none', backgroundColor: itemHoverBgColor, color: itemTextColor }}
      _focusVisible={{
        boxShadow: 'outline',
        outline: 'none',
      }}
      fontWeight={isActive ? 'semibold' : 'medium'}
      fontSize="sm"
      px={3}
      py={2}
      borderRadius="md"
      width="100%"
      minH="40px"
      bg={isActive ? activeBgColor : 'transparent'}
      color={itemTextColor}
      borderLeft="3px solid"
      borderLeftColor={isActive ? activeTextColor : 'transparent'}
      target={external ? '_blank' : '_self'}
      rel={external ? 'noopener noreferrer' : undefined}
      aria-current={isActive ? 'page' : undefined}
    >
      <Box display="flex" alignItems="center">
        <Icon as={icon} mr={3} boxSize={4} color={isActive || isEnvironment ? itemTextColor : mutedIconColor} />
        <Text noOfLines={1}>{label}</Text>
      </Box>
    </ChakraLink>
  );
};

const SidebarGroup = ({ title, isOpen, onToggle, children }) => (
  <Box width="100%">
    <Button
      onClick={onToggle}
      variant="ghost"
      width="100%"
      justifyContent="space-between"
      size="sm"
      fontSize="xs"
      fontWeight="semibold"
      textTransform="uppercase"
      letterSpacing="wider"
      color="gray.500"
      px={2}
      mb={1}
      _hover={{ bg: 'transparent', color: 'inherit' }}
      _active={{ bg: 'transparent' }}
      _focusVisible={{ boxShadow: 'outline' }}
    >
      <Text noOfLines={1}>{title}</Text>
      <Icon as={isOpen ? ChevronDownIcon : ChevronRightIcon} boxSize={4} />
    </Button>
    <Collapse in={isOpen} animateOpacity>
      <VStack align="start" spacing={1} pl={1}>
        {children}
      </VStack>
    </Collapse>
  </Box>
);

const SidebarDivider = () => (
  <Divider my={1} opacity={0.7} />
);

const SIDEBAR_SECTIONS = {
  organizations: [
    '/app/organizations',
    '/app/admin/networks',
  ],
  surveys: [
    '/app/surveys',
    '/app/survey_periods',
  ],
  resources: [
    '/app/articles',
    '/app/widget',
  ],
  consulting: [
    '/app/admin/consultants',
    '/app/admin/leads',
  ],
};

export const getActiveSidebarSection = (pathname) => {
  const matchesPath = (basePath) =>
    pathname === basePath || pathname.startsWith(`${basePath}/`);

  return Object.entries(SIDEBAR_SECTIONS).find(([, paths]) =>
    paths.some(matchesPath)
  )?.[0] || null;
};

const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useUser();
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const hoverBgColor = useColorModeValue('gray.100', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { colorMode } = useColorMode();
  const activeSidebarSection = getActiveSidebarSection(location.pathname);
  const [isSurveysOpen, setIsSurveysOpen] = useState(activeSidebarSection === 'surveys');
  const [isResourcesOpen, setIsResourcesOpen] = useState(activeSidebarSection === 'resources');
  const [isConsultingOpen, setIsConsultingOpen] = useState(activeSidebarSection === 'consulting');
  const [isOrgNetworksOpen, setIsOrgNetworksOpen] = useState(activeSidebarSection === 'organizations');
  const isDecember = new Date().getMonth() === 11;

  useEffect(() => {
    setIsSurveysOpen(activeSidebarSection === 'surveys');
    setIsResourcesOpen(activeSidebarSection === 'resources');
    setIsConsultingOpen(activeSidebarSection === 'consulting');
    setIsOrgNetworksOpen(activeSidebarSection === 'organizations');
  }, [activeSidebarSection]);

  const logoFilename = colorMode === 'dark' ? 'logo-white-md.png' : 'logo-md.png';
  const logoPath = isDecember ? `/images/Christmas/${logoFilename}` : `/images/${logoFilename}`;

  const isLocalhost = process.env.REACT_APP_ENV === "development";
  const isStaging = process.env.REACT_APP_ENV === "staging";
  const consultantUser = isConsultantUser(user);
  const networkRouteMatch = location.pathname.match(/^\/app\/network\/(\d+)/);
  const activeNetworkId = networkRouteMatch?.[1] || null;
  const activeNetworkMembership = activeNetworkId ? getNetworkMembership(user, activeNetworkId) : null;
  const networkMemberships = getNetworkMemberships(user);
  const networkUser = isNetworkUser(user);
  const networkBasePath = activeNetworkId ? `/app/network/${activeNetworkId}` : getDefaultAuthenticatedRoute(user);
  const canManageActiveNetwork = activeNetworkId ? canManageNetwork(user, activeNetworkId) : false;
  const showNetworkNav =
    networkUser &&
    (!consultantUser || Boolean(activeNetworkMembership)) &&
    (!getUserRoles(user).includes('hke_admin') || Boolean(activeNetworkMembership));
  const isHkeAdmin = getUserRoles(user).includes('hke_admin');
  const isOrganizationAppPath =
    location.pathname === '/app/organization' ||
    location.pathname.startsWith('/app/organization/');
  const isOrganizationContext =
    isOrganizationAppPath &&
    !!(user?.organization || user?.user_type === 'tour' || isOrgScopedUser(user));
  const orgAppUser = !isHkeAdmin && (isOrganizationContext || isOrgScopedUser(user));
  const orgAdminUser = isOrganizationAdmin(user);
  const consultantAdminUser = getUserRoles(user).includes('consultant_admin');
  const isTour = user?.user_type === 'tour' || user?.organization?.tour_mode === true;
  const canAccessAdmin = canAccessAdminSection(user);
  const canManageConsultants = canManageConsultantsAdmin(user);
  const canViewLeads = canViewLeadsAdmin(user);
  const internalDashboardHref = getDefaultAuthenticatedRoute(user);
  const showAdminNavItem = canAccessAdmin && internalDashboardHref !== '/app/admin';

  return (
    <Box
      as="aside"
      width="250px"
      bg={bgColor}
      color={textColor}
      height="100vh"
      p={3}
      position="fixed"
      top="0"
      left="0"
      zIndex="100"
      borderRight="1px solid"
      borderRightColor={borderColor}
      display="flex"
      flexDirection="column"
    >
      <Box mb={5} py={2} px={3} textAlign="center" flexShrink={0}>
        <img src={logoPath} alt="HKE Learning" style={{ width: '100%', maxWidth: '160px', height: 'auto', margin: '0 auto' }} />
      </Box>

      <VStack align="start" spacing={2} overflowY="auto" overflowX="hidden" flex="1" minH={0} pr={1}>
        {showNetworkNav ? (
          <>
            <NavItem
              href={networkBasePath}
              icon={FaTachometerAlt}
              label={t('sidebar.dashboard')}
              hoverBgColor={hoverBgColor}
              isActive={activeNetworkId
                ? location.pathname === networkBasePath || location.pathname === `${networkBasePath}/report/overview`
                : location.pathname === '/app/network/select'}
            />
            {networkMemberships.length > 1 ? (
              <NavItem
                href="/app/network/select"
                icon={FaBuilding}
                label="Switch network"
                hoverBgColor={hoverBgColor}
                isActive={location.pathname === '/app/network/select'}
              />
            ) : null}
            {activeNetworkId ? (
              <>
                <NavItem
                  href={`${networkBasePath}/report/organizations`}
                  icon={FaUsers}
                  label="Schools"
                  hoverBgColor={hoverBgColor}
                  isActive={
                    location.pathname.startsWith(`${networkBasePath}/report/organizations`) ||
                    location.pathname.startsWith(`${networkBasePath}/organizations`)
                  }
                />
                <NavItem
                  href={`${networkBasePath}/results`}
                  icon={FaPoll}
                  label="School reports"
                  hoverBgColor={hoverBgColor}
                  isActive={location.pathname.startsWith(`${networkBasePath}/results`)}
                />
                <NavItem
                  href={`${networkBasePath}/report/pairing`}
                  icon={FaUserFriends}
                  label="Pairing"
                  hoverBgColor={hoverBgColor}
                  isActive={location.pathname.startsWith(`${networkBasePath}/report/pairing`)}
                />
                <NavItem
                  href={`${networkBasePath}/report/factors`}
                  icon={FaNewspaper}
                  label="Factors"
                  hoverBgColor={hoverBgColor}
                  isActive={location.pathname.startsWith(`${networkBasePath}/report/factors`)}
                />
                <NavItem
                  href={`${networkBasePath}/report/schools-factors`}
                  icon={FaTable}
                  label="Schools × Factors"
                  hoverBgColor={hoverBgColor}
                  isActive={location.pathname.startsWith(`${networkBasePath}/report/schools-factors`)}
                />
                {canManageActiveNetwork ? (
                  <>
                    <NavItem
                      href={`${networkBasePath}/users`}
                      icon={FaUser}
                      label="Users"
                      hoverBgColor={hoverBgColor}
                      isActive={location.pathname.startsWith(`${networkBasePath}/users`)}
                    />
                    <NavItem
                      href={`${networkBasePath}/network-settings`}
                      icon={FaWrench}
                      label="Network Settings"
                      hoverBgColor={hoverBgColor}
                      isActive={location.pathname.startsWith(`${networkBasePath}/network-settings`)}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </>
        ) : orgAppUser ? (
          <>
            <NavItem
              href="/app/organization"
              icon={FaTachometerAlt}
              label={t('sidebar.dashboard')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname === '/app/organization'}
            />
            {orgAdminUser && !isTour ? (
              <>
                <NavItem
                  href="/app/organization/users"
                  icon={FaUser}
                  label={t('organization.users', 'Users')}
                  hoverBgColor={hoverBgColor}
                  isActive={location.pathname.startsWith('/app/organization/users')}
                />
                <NavItem
                  href="/app/organization/organization-settings"
                  icon={FaWrench}
                  label={t('organization.settings', 'School Settings')}
                  hoverBgColor={hoverBgColor}
                  isActive={location.pathname.startsWith('/app/organization/organization-settings')}
                />
              </>
            ) : null}
            <NavItem
              href="/app/organization/surveys"
              icon={FaPoll}
              label={t('organization.surveys', 'Surveys')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith('/app/organization/surveys')}
            />
            <NavItem
              href="/app/organization/results"
              icon={FaUsers}
              label={t('organization.results', 'Results')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith('/app/organization/results')}
            />
            <NavItem
              href="/app/organization/workbook"
              icon={FaClipboardList}
              label={t('organization.workbook.title', 'Workbook')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith('/app/organization/workbook')}
            />
          </>
        ) : consultantUser ? (
          <>
            <NavItem
              href="/app/consultant/dashboard"
              icon={FaTachometerAlt}
              label={t('sidebar.dashboard')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith('/app/consultant/dashboard')}
            />
            <NavItem
              href="/app/consultant/organizations"
              icon={FaUsers}
              label={t('sidebar.organization')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith('/app/consultant/organizations')}
            />
            <NavItem
              href="/app/consultant/networks"
              icon={FaBuilding}
              label="Networks"
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith('/app/consultant/networks')}
            />
            <NavItem
              href="/app/consultant/leads"
              icon={FaUserFriends}
              label={t('sidebar.leads', 'Leads')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith('/app/consultant/leads')}
            />
            <NavItem
              href="/app/consultant/users"
              icon={FaUser}
              label={t('sidebar.users')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith('/app/consultant/users')}
            />
            {consultantAdminUser ? (
              <NavItem
                href="/app/consultant/consultant-settings"
                icon={FaWrench}
                label="Consultant Settings"
                hoverBgColor={hoverBgColor}
                isActive={location.pathname.startsWith('/app/consultant/consultant-settings')}
              />
            ) : null}
          </>
        ) : (
          <>
            <NavItem
              href={internalDashboardHref}
              icon={FaTachometerAlt}
              label={t('sidebar.dashboard')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith(internalDashboardHref)}
            />

            {isHkeAdmin ? (
              <SidebarGroup
                title={t('sidebar.organizations_networks_group', 'Schools')}
                isOpen={isOrgNetworksOpen}
                onToggle={() => setIsOrgNetworksOpen((prev) => !prev)}
              >
                <NavItem
                  href="/app/organizations"
                  icon={FaUsers}
                  label={t('sidebar.organization')}
                  hoverBgColor={hoverBgColor}
                  isActive={location.pathname.startsWith('/app/organizations')}
                />
                <NavItem
                  href="/app/admin/networks"
                  icon={FaBuilding}
                  label="Networks"
                  hoverBgColor={hoverBgColor}
                  isActive={location.pathname.startsWith('/app/admin/networks')}
                />
              </SidebarGroup>
            ) : (
              <NavItem
                href="/app/organizations"
                icon={FaUsers}
                label={t('sidebar.organization')}
                hoverBgColor={hoverBgColor}
                isActive={location.pathname.startsWith('/app/organizations')}
              />
            )}

            <SidebarGroup
              title={t('sidebar.surveys_group', 'Surveys')}
              isOpen={isSurveysOpen}
              onToggle={() => setIsSurveysOpen((prev) => !prev)}
            >
              <NavItem
                href="/app/surveys"
                icon={FaClipboardList}
                label={t('sidebar.surveys')}
                hoverBgColor={hoverBgColor}
                isActive={location.pathname.startsWith('/app/surveys')}
              />
              <NavItem
                href="/app/survey_periods"
                icon={FaRegCalendarAlt}
                label={t('sidebar.survey_periods')}
                hoverBgColor={hoverBgColor}
                isActive={location.pathname.startsWith('/app/survey_periods')}
              />
            </SidebarGroup>

            <NavItem
              href="/app/messages"
              icon={FaEnvelope}
              label={t('sidebar.messages')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith('/app/messages')}
            />

            <NavItem
              href="/app/users"
              icon={FaUser}
              label={t('sidebar.users')}
              hoverBgColor={hoverBgColor}
              isActive={location.pathname.startsWith('/app/users')}
            />

            <SidebarDivider />

            <SidebarGroup
              title={t('sidebar.resources_group', 'Resources')}
              isOpen={isResourcesOpen}
              onToggle={() => setIsResourcesOpen((prev) => !prev)}
            >
              <NavItem
                href="/app/articles"
                icon={FaNewspaper}
                label={t('sidebar.articles', 'Articles')}
                hoverBgColor={hoverBgColor}
                isActive={location.pathname.startsWith('/app/articles')}
              />
              <NavItem
                href="/app/widget"
                icon={FaPuzzlePiece}
                label={t('sidebar.widgets', 'Widgets')}
                hoverBgColor={hoverBgColor}
                isActive={location.pathname.startsWith('/app/widget')}
              />
            </SidebarGroup>

            <SidebarDivider />

            {(canManageConsultants || canViewLeads) ? (
              <SidebarGroup
                title={t('sidebar.consulting_group', 'Consulting')}
                isOpen={isConsultingOpen}
                onToggle={() => setIsConsultingOpen((prev) => !prev)}
              >
                {canManageConsultants ? (
                  <NavItem
                    href="/app/admin/consultants"
                    icon={FaBuilding}
                    label={t('sidebar.consultants', 'Consultants')}
                    hoverBgColor={hoverBgColor}
                    isActive={location.pathname.startsWith('/app/admin/consultants')}
                  />
                ) : null}
                {canViewLeads ? (
                  <NavItem
                    href="/app/admin/leads"
                    icon={FaUserFriends}
                    label={t('sidebar.leads', 'Leads')}
                    hoverBgColor={hoverBgColor}
                    isActive={location.pathname.startsWith('/app/admin/leads')}
                  />
                ) : null}
              </SidebarGroup>
            ) : null}

            {showAdminNavItem ? (
              <NavItem
                href="/app/admin"
                icon={FaWrench}
                label={t('sidebar.admin')}
                hoverBgColor={hoverBgColor}
                isActive={location.pathname === '/app/admin'}
              />
            ) : null}

            {isLocalhost && (
              <>
                <SidebarDivider />
                <NavItem
                  href="http://localhost:4000/letter_opener"
                  icon={HiInboxArrowDown}
                  label='Simulated Email'
                  hoverBgColor="orange"
                  external={true}
                  tone="environment"
                />
              </>
            )}

            {isStaging && (
              <>
                <SidebarDivider />
                <NavItem
                  href="https://testing.hke.global/mailhog"
                  icon={HiInboxArrowDown}
                  label='Simulated Email'
                  hoverBgColor="red"
                  external={true}
                  tone="environment"
                />
              </>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
};

export default Sidebar;
